/**
 * Hover popover for a month cell in the FP Monthly COGS Tracker grid.
 *
 * Wraps the cell content. On hover the popover shows: month label,
 * status, products to review, source chip, last-detected timestamp.
 * The wrapped child stays clickable - if its parent renders a link,
 * that link still opens on click.
 *
 * Built on MUI ``Tooltip`` with a rich JSX ``title`` instead of
 * ``Popover``: Tooltip doesn't intercept clicks, doesn't trigger a
 * document-level layout shift (scrollbar flash on the host table),
 * and handles the cell→popover hover hysteresis via ``leaveDelay`` so
 * the user can move the cursor onto the bubble without it closing.
 */
import React from 'react'
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import type { CogsMonthCell } from './types'

interface CogsCellPopoverProps {
  monthLabel: string
  cell: CogsMonthCell | undefined
  children: React.ReactElement
}

const formatDetected = (iso: string | null): string | null => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const sourceLabel = (source: CogsMonthCell['source']): string | null => {
  if (source === 'google') return 'Google Sheet'
  if (source === 'legacy_excel') return 'Legacy Excel'
  return null
}

const CogsCellPopover: React.FC<CogsCellPopoverProps> = ({
  monthLabel,
  cell,
  children,
}) => {
  const detected = formatDetected(cell?.detected_at ?? null)
  const source = sourceLabel(cell?.source ?? null)
  const hasBody =
    !!cell?.status || !!cell?.products_to_review || !!source || !!detected

  if (!hasBody) {
    return <>{children}</>
  }

  const title = (
    <Stack spacing={0.75} sx={{ p: 0.5 }}>
      <Typography variant="caption" sx={{ opacity: 0.75 }}>
        {monthLabel}
      </Typography>
      {cell?.status ? (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {cell.status}
        </Typography>
      ) : null}
      {cell?.products_to_review ? (
        <Typography variant="caption" sx={{ opacity: 0.85 }}>
          {cell.products_to_review} product
          {cell.products_to_review === 1 ? '' : 's'} to review
        </Typography>
      ) : null}
      {source ? (
        <Box>
          <Chip
            label={source}
            size="small"
            variant="outlined"
            sx={{
              color: 'common.white',
              borderColor: 'rgba(255,255,255,0.6)',
              height: 20,
              fontSize: '0.7rem',
            }}
          />
        </Box>
      ) : null}
      {detected ? (
        <Typography variant="caption" sx={{ opacity: 0.65 }}>
          Last detected {detected}
        </Typography>
      ) : null}
    </Stack>
  )

  return (
    <Tooltip
      title={title}
      placement="bottom"
      arrow
      enterDelay={0}
      leaveDelay={100}
      slotProps={{
        tooltip: { sx: { maxWidth: 260, p: 1 } },
      }}
    >
      {children}
    </Tooltip>
  )
}

export default CogsCellPopover
